import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Zap, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (user) {
      const to = location.state?.from || "/admin/blog";
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await login(form.email.trim(), form.password);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate(location.state?.from || "/admin/blog", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="admin-brand">
          <span className="flex items-center justify-center w-10 h-10 rounded-md bg-gradient-to-br from-[#FF8C00] to-[#FF5A00] shadow-[0_0_24px_rgba(255,140,0,0.45)]">
            <Zap className="w-5 h-5 text-[#0A0A0C]" strokeWidth={3} />
          </span>
          <span className="font-display font-black text-2xl text-white">
            perfo<span className="text-[#FF8C00]">range</span>
          </span>
        </Link>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-white/10 bg-[#0C0C10] p-8 shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">
            <Lock className="w-3 h-3" /> Admin Console
          </div>
          <h1 className="mt-3 font-display font-black text-3xl text-white">Sign in</h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">
            Editorial access for the Perforange Journal. Authorized personnel only.
          </p>

          <div className="mt-7 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">Email</label>
              <input
                type="email"
                required
                autoComplete="username"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="admin-login-email"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="admin-login-password"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm"
              />
            </div>

            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#FF8C00] text-[#0A0A0C] font-black text-sm uppercase tracking-widest hover:bg-[#FFA333] disabled:opacity-60 transition-all"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>

        <p className="mt-6 text-[11px] text-center text-[#6a6a70] font-mono-tech uppercase tracking-widest">
          Brute-force protected · Sessions expire after 12 hours
        </p>
      </div>
    </div>
  );
}
